import React, { useState, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import Navbar from "../components/common/Navbar";
import "../styles/Users.css";
import "../styles/Documents.css";
import AddIcon from "@mui/icons-material/Add";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";

import RoleTable from "../components/role-table/RoleTable";
import { AuthContext } from "../context/Auth/AuthContext";
import AddRole from "../components/role-table/AddRole";
import BulkDeletePopup from "../components/role-table/BulkDeletePopup";

import baseUrl from "../config/baseUrl";
import roleData from "../api/user";
import { convertToUTC } from "../utils/formatDate";
import PageInfoTooltip from "../components/common/PageInfoTooltip";

const Roles = () => {
  return <RoleComponent />;
};

const RoleComponent = () => {
  const [pageCount, setPageCount] = useState(0);
  const [showAddRoleForm, setShowAddRoleForm] = useState(false);
  const [roleAdded, setRoleAdded] = useState(0);
  const [bulkDeleteRole, setBulkDeleteRole] = useState(false);
  const [bulkDeleteRoleIds, setBulkDeleteRoleIds] = useState();

  const { token, is_admin, user_id } = useContext(AuthContext);

  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [globalFilter, setGlobalFilter] = useState("");
  const [showFilterButton, setShowFilterButton] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      "roleData",
      roleAdded,
      globalFilter,
      currentPage,
      columnFilters,
      pageSize,
      sorting,
      updatedAt,
      is_admin,
      user_id,
      token,
    ],
    queryFn: async () => {
      const offset = currentPage - 1;
      const filterParams = {};

      columnFilters.forEach((filterItem) => {
        filterParams[filterItem.id] = filterItem.value;
      });

      const sortParam = sorting
        .map((sortItem) => `${sortItem.id}=${sortItem.desc ? "desc" : "asc"}`)
        .join(",");

      const params = {
        isAdmin: is_admin,
        userId: user_id,
        limit: pageSize,
        offset,
        ...filterParams,
        global_search: globalFilter,
        sort: sortParam,
        ...(updatedAt ? { updated_at: convertToUTC(updatedAt) } : {}),
      };

      const queryString = new URLSearchParams(params).toString();
      const url = `${baseUrl}/api/get-roles?${queryString}`;

      console.log("Calling get-roles API:", url);

      const response = await roleData({ token, api: url });

      if (response.code !== 200) {
        throw new Error("Network response was not ok");
      }

      setTotalPages(Math.ceil((response?.user_count || 0) / pageSize));
      return response;
    },
    keepPreviousData: true,
  });

  const onAddRoleButtonClick = () => {
    setShowAddRoleForm((prev) => !prev);
  };

  const setRoleAddedToTrue = () => {
    setRoleAdded((count) => count + 1);
  };

  return (
    <div className="user-container">
      <div className="navbar">
        <Navbar />
      </div>

      <div className="user-table">
        {showAddRoleForm ? (
          <AddRole
            onAddRoleButtonClick={onAddRoleButtonClick}
            setRoleAddedToTrue={setRoleAddedToTrue}
          />
        ) : (
          <div className="user-content settings-content">
            {/* No breadcrumb - Roles is a plain list page with no deeper
                detail/viewer state, and the Sidebar already shows the
                current location. */}
            <div className="documents-page-header flex items-center justify-between gap-3 py-3shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-nbl-primary text-white">
                  <SecurityOutlinedIcon sx={{ fontSize: 18 }} />
                </span>
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 min-w-0">
                    <h1 className="text-lg font-semibold text-gray-900 leading-tight truncate min-w-0">
                      Roles
                    </h1>
                    <PageInfoTooltip title="Roles" description="Manage roles and their permissions" />
                  </span>
                  <p className="text-xs text-gray-500 leading-tight truncate">
                    Manage roles and their permissions
                  </p>
                </span>
              </div>
              {is_admin === "1" && (
                <button className="add-document shrink-0" onClick={onAddRoleButtonClick}>
                  <AddIcon sx={{ fontSize: 18 }} />
                  Add Role
                </button>
              )}
            </div>

            <div className="user-table-content">
              <RoleTable
                pageCount={pageCount}
                loading={isLoading}
                roleTableData={data?.data || []}
                setRoleAddedToTrue={setRoleAddedToTrue}
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                totalPages={totalPages}
                showFilterButton={showFilterButton}
                setShowFilterButton={setShowFilterButton}
                setPageSize={setPageSize}
                isError={isError}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                sorting={sorting}
                setSorting={setSorting}
                setBulkDeleteRoleIds={setBulkDeleteRoleIds}
                setBulkDeleteRole={setBulkDeleteRole}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                updatedAt={updatedAt}
                setUpdatedAt={setUpdatedAt}
              />
            </div>
          </div>
        )}
      </div>

      {bulkDeleteRole && (
        <BulkDeletePopup
          Rolebulkid={bulkDeleteRoleIds}
          bulkDeleteRole={bulkDeleteRole}
          setBulkDeleteRole={setBulkDeleteRole}
          setRowSelection={setRowSelection}
          setRoleAddedToTrue={setRoleAddedToTrue}
        />
      )}
    </div>
  );
};

export default Roles;
